import React, { useEffect, useRef, useState } from "react";
import api, { formatPHP, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, AlertTriangle, XCircle, Users, FileSpreadsheet, History } from "lucide-react";

/* Bulk employee import — upload, review every row, then commit.
 * Nothing is written until HR sees exactly what will happen. */

const TONE = {
    ok: { icon: CheckCircle2, cls: "text-emerald-600", row: "", label: "Ready" },
    warning: { icon: AlertTriangle, cls: "text-amber-600", row: "bg-amber-50/40", label: "Check" },
    error: { icon: XCircle, cls: "text-red-600", row: "bg-red-50/40", label: "Blocked" },
};

export default function EmployeeImport() {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [filter, setFilter] = useState("all");
    const [past, setPast] = useState([]);
    const fileRef = useRef(null);

    const loadHistory = () => api.get("/hris/import/history").then((r) => setPast(r.data.items || [])).catch(() => {});
    useEffect(() => { loadHistory(); }, []);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true); setResult(null); setPreview(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/hris/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
            setFilter(data.error ? "error" : "all");
            toast.success(`Read ${data.total} row${data.total === 1 ? "" : "s"} from ${data.filename}`);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not read that file");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const commit = async () => {
        const importable = preview.total - preview.error;
        if (!importable) return toast.error("Every row has a blocking problem — fix the file and upload again.");
        if (!window.confirm(`Create ${importable} employee${importable === 1 ? "" : "s"}?${preview.error ? ` ${preview.error} blocked row${preview.error === 1 ? "" : "s"} will be skipped.` : ""}`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/hris/import/commit", { rows: preview.rows, skip_errors: true });
            setResult(data); setPreview(null); loadHistory();
            toast.success(`${data.created_count} employee${data.created_count === 1 ? "" : "s"} added`);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Import failed");
        } finally { setBusy(false); }
    };

    const shown = (preview?.rows || []).filter((r) => filter === "all" || r.status === filter);

    return (
        <div>
            <PageHeader
                title="Import Employees"
                subtitle="Upload a spreadsheet of your existing headcount. Every row is checked before anything is saved."
                actions={
                    <div className="flex items-center gap-2">
                        <a className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-2" href="/api/hris/import/template"
                           onClick={async (e) => {
                               // Route is auth-gated, so fetch with the token and save the blob.
                               e.preventDefault();
                               try {
                                   const r = await api.get("/hris/import/template", { responseType: "blob" });
                                   const url = URL.createObjectURL(r.data);
                                   const a = document.createElement("a");
                                   a.href = url; a.download = "sahod-employee-import-template.csv"; a.click();
                                   URL.revokeObjectURL(url);
                               } catch { toast.error("Could not download the template"); }
                           }} data-testid="import-template">
                            <Download className="size-4" /> Download template
                        </a>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                                disabled={busy} onClick={() => fileRef.current?.click()} data-testid="import-upload">
                            <Upload className="size-4" /> {busy ? "Reading…" : "Upload file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
                               onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file" />
                    </div>
                } />

            {!preview && !result && (
                <Surface className="p-8 text-center" data-testid="import-empty">
                    <FileSpreadsheet className="size-10 mx-auto text-muted-foreground/40 mb-3" />
                    <h3 className="font-semibold">Start with a CSV or Excel file</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-lg mx-auto">
                        Column names are matched automatically — "Employee No", "empno" and "Employee Number" all work.
                        Departments, branches and managers are matched by name. Download the template if you'd rather start from ours.
                    </p>
                </Surface>
            )}

            {preview && (
                <div className="space-y-3">
                    <Surface className="p-4">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { k: "all", n: preview.total, label: "rows", cls: "bg-secondary" },
                                    { k: "ok", n: preview.ok, label: "ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                                    { k: "warning", n: preview.warning, label: "need a look", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                                    { k: "error", n: preview.error, label: "blocked", cls: "bg-red-50 text-red-700 border-red-200" },
                                ].map((s) => (
                                    <button key={s.k} onClick={() => setFilter(s.k)}
                                            className={`px-3 py-1.5 rounded-md border text-sm ${s.cls} ${filter === s.k ? "ring-2 ring-primary/40" : ""}`}
                                            data-testid={`import-filter-${s.k}`}>
                                        <b className="font-mono">{s.n}</b> {s.label}
                                    </button>
                                ))}
                            </div>
                            <button className="h-9 px-4 rounded-md bg-primary text-white text-sm font-semibold disabled:opacity-50"
                                    disabled={busy || preview.total === preview.error} onClick={commit} data-testid="import-commit">
                                <Users className="size-4 inline mr-1.5 -mt-0.5" />
                                Import {preview.total - preview.error} employee{preview.total - preview.error === 1 ? "" : "s"}
                            </button>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2.5">
                            Matched columns: {preview.detected_columns.join(", ")}
                            {preview.ignored_columns?.length > 0 && (
                                <span className="block mt-0.5">Ignored: {preview.ignored_columns.join(", ")}</span>
                            )}
                        </div>
                        {preview.error > 0 && (
                            <div className="mt-2.5 text-xs bg-red-50 border border-red-200 text-red-800 rounded-md p-2.5">
                                Blocked rows are skipped, not imported. Fix them in the file and upload again to add them.
                            </div>
                        )}
                    </Surface>

                    <Surface className="p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40">
                                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="px-3 py-2.5">Row</th><th className="px-3 py-2.5">Employee</th>
                                        <th className="px-3 py-2.5">Position</th><th className="px-3 py-2.5">Department</th>
                                        <th className="px-3 py-2.5 text-right">Monthly</th><th className="px-3 py-2.5">Findings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map((r) => {
                                        const tone = TONE[r.status];
                                        const Icon = tone.icon;
                                        return (
                                            <tr key={r.row} className={`border-t border-border ${tone.row}`} data-testid={`import-row-${r.row}`}>
                                                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.row}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <Icon className={`size-3.5 shrink-0 ${tone.cls}`} />
                                                        <span className="font-medium">{r.display.name}</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground font-mono">{r.display.employee_no}</div>
                                                </td>
                                                <td className="px-3 py-2 text-muted-foreground">{r.display.position}</td>
                                                <td className="px-3 py-2 text-muted-foreground">{r.display.department}</td>
                                                <td className="px-3 py-2 text-right font-mono">{r.display.basic_monthly ? formatPHP(r.display.basic_monthly) : "—"}</td>
                                                <td className="px-3 py-2">
                                                    {r.errors.map((m, i) => <div key={`e${i}`} className="text-xs text-red-700">✕ {m}</div>)}
                                                    {r.warnings.map((m, i) => <div key={`w${i}`} className="text-xs text-amber-700">! {m}</div>)}
                                                    {!r.errors.length && !r.warnings.length && <span className="text-xs text-emerald-700">Looks good</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!shown.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">No rows in this filter.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </Surface>
                </div>
            )}

            {result && (
                <Surface className="p-6" data-testid="import-result">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="size-5" /> Import complete</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        <b className="text-foreground font-mono">{result.created_count}</b> employees added
                        {result.skipped_count > 0 && <> · <b className="text-foreground font-mono">{result.skipped_count}</b> skipped</>}
                    </p>
                    {result.skipped_count > 0 && (
                        <div className="mt-3 text-xs space-y-1 max-h-52 overflow-auto border border-border rounded-md p-3">
                            {result.skipped.map((s, i) => (
                                <div key={i} className="text-amber-800">Row {s.row} — {s.name}: {s.reason}</div>
                            ))}
                        </div>
                    )}
                    <a className="inline-block mt-4 h-9 px-4 rounded-md bg-primary text-white text-sm leading-9" href="/hris/employees">View employees</a>
                </Surface>
            )}

            {past.length > 0 && (
                <Surface className="p-4 mt-4">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2"><History className="size-4" /> Past imports</h3>
                    <div className="space-y-1 text-sm">
                        {past.map((b) => (
                            <div key={b.id} className="flex items-center justify-between border-b border-border last:border-0 py-1.5">
                                <span className="text-muted-foreground text-xs">{formatDateTime(b.imported_at)}</span>
                                <span className="font-mono text-xs">
                                    <b className="text-emerald-700">+{b.created_count}</b>
                                    {b.skipped_count > 0 && <span className="text-amber-700 ml-2">{b.skipped_count} skipped</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                </Surface>
            )}
        </div>
    );
}
